import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/FolderSpecial';
import LocalShippingIcon from '@mui/icons-material/DeliveryDining';
import ScienceIcon from '@mui/icons-material/Biotech';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ForumIcon from '@mui/icons-material/Forum';
import ChatIcon from '@mui/icons-material/Chat';
import { labAPI, subscriptionsAPI, billsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotifyContext';
import LabExtendedFeatures from './LabExtendedFeatures';
import { useMessagingInbox } from '../../hooks/useMessagingInbox';
import { formatLocalDate, formatLocalDateTime, localCalendarYearMonth } from '../../utils/formatLocalDate';
import { filterBySearch } from '../../utils/tableSearch';
import TableToolbar from '../TableToolbar';
import Pagination from '../Pagination';
import Modal from '../Modal';
import SubscribeCheckoutModal from '../SubscribeCheckoutModal';
import DashboardSectionLoader from '../DashboardSectionLoader';
import GradientSpinner from '../GradientSpinner';
import AccountSettingsPanel from '../AccountSettingsPanel';
import LabTaskTracker from './LabTaskTracker';
import { MessagingConversationListItem, MessagingMessageRow } from '../MessagingUI';
import { userDisplayLabel } from '../../utils/userDisplayLabel';
import '../../App.css';

function LabPlanCardsGrid({ labPlans, labSubscriptionMeta, onPlanClick, gridStyle }) {
  const baseGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
    marginTop: '16px',
    ...gridStyle
  };
  return (
    <div style={baseGrid}>
      {labPlans.map((plan) => {
        const isCurrentPlan =
          labSubscriptionMeta.currentPlanId &&
          String(plan._id) === labSubscriptionMeta.currentPlanId;
        const showAsSubscribed = isCurrentPlan && labSubscriptionMeta.hasPaidPlanAssigned;
        return (
          <div
            key={plan._id}
            style={{
              padding: '14px',
              borderRadius: '10px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)'
            }}
          >
            <p style={{ margin: '0 0 6px 0', fontWeight: 700, color: 'var(--logo-cyan-teal)' }}>{plan.name}</p>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              From ${plan.priceMonthly ?? 0}/mo
              {plan.priceYearly > 0 ? ` · $${plan.priceYearly}/yr` : ''}
            </p>
            {showAsSubscribed ? (
              <button
                type="button"
                className="btn btn-outline"
                disabled
                style={{ width: '100%', padding: '8px', fontSize: '0.8125rem', opacity: 0.95, cursor: 'default' }}
              >
                Your plan
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%', padding: '8px', fontSize: '0.8125rem' }}
                onClick={() => onPlanClick(plan._id)}
              >
                {labSubscriptionMeta.hasStripeSubscription ? 'Change plan' : 'Subscribe'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** YYYY-MM → short month label (UTC) */
function formatAnalyticsMonth(ym) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return ym || '—';
  const [y, m] = String(ym).split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const LAB_JOB_STATUS_LABELS = {
  open: 'Open',
  bidding: 'Bidding',
  pending_acceptance: 'Pending acceptance',
  assigned: 'Assigned',
  'in-progress': 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

function labJobStatusLabel(status) {
  if (!status) return '—';
  return LAB_JOB_STATUS_LABELS[status] || status;
}

const LabDashboard = ({ activeTab: propTab }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError, showInfo } = useNotify();
  const [pickupRequests, setPickupRequests] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reagentRequests, setReagentRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [communityThreads, setCommunityThreads] = useState([]);
  const [newsAnnouncements, setNewsAnnouncements] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [bills, setBills] = useState([]);
  const dashboard = { pickupRequests, jobs, reagentRequests, reports };
  const [activeTab, setActiveTab] = useState(propTab || 'overview');
  const [formData, setFormData] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);
  const [showReagentModal, setShowReagentModal] = useState(false);
  const [showAddClinicModal, setShowAddClinicModal] = useState(false);
  const [editClinicDraft, setEditClinicDraft] = useState(null);
  const [editClinicSaving, setEditClinicSaving] = useState(false);
  const [disconnectClinicTarget, setDisconnectClinicTarget] = useState(null);
  const [disconnectClinicBusy, setDisconnectClinicBusy] = useState(false);
  const [demographicsStats, setDemographicsStats] = useState(null);
  const [demographicsStatsLoading, setDemographicsStatsLoading] = useState(false);
  const [demoAnalyticsMonth, setDemoAnalyticsMonth] = useState(() =>
    typeof window !== 'undefined' ? localCalendarYearMonth() : ''
  );
  const [demoAnalyticsDay, setDemoAnalyticsDay] = useState('');
  const [demoAnalyticsClinicId, setDemoAnalyticsClinicId] = useState('');
  const [demoAnalyticsPanel, setDemoAnalyticsPanel] = useState('all');
  const [demoPanelOptions, setDemoPanelOptions] = useState([]);
  const [demoFilteredStats, setDemoFilteredStats] = useState(null);
  const [demoFilteredLoading, setDemoFilteredLoading] = useState(false);
  const [showUploadReportModal, setShowUploadReportModal] = useState(false);
  const [labLogoUrl, setLabLogoUrl] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('labLogoUrl') : null
  );
  const [labPlans, setLabPlans] = useState([]);
  const [subscribeModal, setSubscribeModal] = useState({ open: false, planId: null });
  const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(false);

  const {
    conversations,
    messages: chatMessages,
    selectedConvId: selectedConv,
    setSelectedConvId: setSelectedConv,
    messageText,
    setMessageText,
    sendMessage,
    startConversation,
    loading: messagingLoading
  } = useMessagingInbox({
    enabled: activeTab === 'messaging',
    filterConversation: (c) => (c.type || '').startsWith('lab-')
  });

  // Pagination states
  const [pickupPage, setPickupPage] = useState(1);
  const [jobsPage, setJobsPage] = useState(1);
  const [reagentsPage, setReagentsPage] = useState(1);
  const [reportsPage, setReportsPage] = useState(1);
  const [labPickupSearch, setLabPickupSearch] = useState('');
  const [labPickupStatusFilter, setLabPickupStatusFilter] = useState('');
  const [labJobSearch, setLabJobSearch] = useState('');
  const [labJobStatusFilter, setLabJobStatusFilter] = useState('');
  const [labReagentSearch, setLabReagentSearch] = useState('');
  const [labReagentStatusFilter, setLabReagentStatusFilter] = useState('');
  const [labReportSearch, setLabReportSearch] = useState('');
  const [labBillsSearch, setLabBillsSearch] = useState('');
  const [labClinicsSearch, setLabClinicsSearch] = useState('');
  const itemsPerPage = 10;

  useEffect(() => setPickupPage(1), [labPickupSearch, labPickupStatusFilter]);
  useEffect(() => setJobsPage(1), [labJobSearch, labJobStatusFilter]);
  useEffect(() => setReagentsPage(1), [labReagentSearch, labReagentStatusFilter]);
  useEffect(() => setReportsPage(1), [labReportSearch]);

  useEffect(() => {
    if (propTab) setActiveTab(propTab);
  }, [propTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, pickupsRes, jobsRes, reagentsRes, reportsRes, clinicsRes] = await Promise.all([
        labAPI.getDashboard(),
        labAPI.getPickupRequests(),
        labAPI.getJobs(),
        labAPI.getReagentRequests(),
        labAPI.getReports(),
        labAPI.getClinics()
      ]);
      if (dashRes.data?.pickupRequests) setPickupRequests(dashRes.data.pickupRequests);
      if (dashRes.data?.jobs) setJobs(dashRes.data.jobs);
      if (dashRes.data?.reagentRequests) setReagentRequests(dashRes.data.reagentRequests);
      if (dashRes.data?.reports) setReports(dashRes.data.reports);
      if (pickupsRes.data?.requests) setPickupRequests(pickupsRes.data.requests);
      if (jobsRes.data?.jobs) setJobs(jobsRes.data.jobs);
      if (reagentsRes.data?.requests) setReagentRequests(reagentsRes.data.requests);
      if (reportsRes.data?.reports) setReports(reportsRes.data.reports);
      if (clinicsRes.data?.clinics) setClinics(clinicsRes.data.clinics);
    } catch (err) {
      console.error('Lab fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionAndBills = async () => {
    try {
      const [subRes, billsRes, plansRes] = await Promise.all([
        subscriptionsAPI.getSubscription(),
        billsAPI.getBills(),
        subscriptionsAPI.getPlans({ role: 'lab' })
      ]);
      if (subRes.data) setSubscription(subRes.data);
      if (billsRes.data?.bills) setBills(billsRes.data.bills);
      if (plansRes.data?.byRole?.lab?.length) setLabPlans(plansRes.data.byRole.lab);
      else if (plansRes.data?.plans?.length) {
        setLabPlans(plansRes.data.plans.filter((p) => p.role === 'lab'));
      }
    } catch (err) {
      console.error('Fetch subscription/bills error:', err);
    }
  };

  const labSubscriptionMeta = useMemo(() => {
    const sub = subscription?.subscription;
    if (!sub) {
      return {
        currentPlanId: null,
        hasStripeSubscription: false,
        hasPaidPlanAssigned: false,
        planDisplayName: null,
        billingCycle: null,
        endDate: null
      };
    }
    const planDoc = sub.planId;
    const rawPlanId = planDoc?._id ?? sub.planId;
    const currentPlanId = rawPlanId != null ? String(rawPlanId) : null;
    const hasStripeSubscription = Boolean(sub.stripeSubscriptionId);
    const paidTypes = new Set(['basic', 'pro', 'paid', 'custom']);
    const hasPaidPlanAssigned =
      hasStripeSubscription || (paidTypes.has(sub.type) && !!currentPlanId);
    const planDisplayName =
      typeof planDoc?.name === 'string' ? planDoc.name : null;
    return {
      currentPlanId,
      hasStripeSubscription,
      hasPaidPlanAssigned,
      planDisplayName,
      billingCycle: sub.billingCycle,
      endDate: sub.endDate
    };
  }, [subscription]);

  const openSubscribeModal = (planId) => {
    setSubscribeModal({ open: true, planId: planId || null });
  };

  const startBillCheckout = async (billId) => {
    try {
      const { data } = await billsAPI.createCheckoutSession(billId, { returnTab: activeTab });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      showError(data?.message || 'Could not start Stripe checkout');
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Could not start Stripe checkout');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const billCheckout = params.get('billCheckout');
    const billId = params.get('billId');
    const tab = params.get('tab') || 'overview';
    if (checkout !== 'success' && checkout !== 'cancel' && billCheckout !== 'success' && billCheckout !== 'cancel') {
      return;
    }

    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?tab=${encodeURIComponent(tab)}`
    );

    if (billCheckout === 'cancel') {
      showInfo('Stripe payment canceled.');
      return;
    }
    if (billCheckout === 'success') {
      (async () => {
        await fetchSubscriptionAndBills();
        if (billId) showSuccess(`Invoice ${billId.slice(-6)} paid successfully.`);
        else showSuccess('Invoice paid successfully.');
      })();
      return;
    }

    if (checkout === 'cancel') {
      showInfo('Checkout was canceled.');
      return;
    }

    showInfo('Payment received. Confirming your subscription…');
    let cancelled = false;

    const refreshBundle = async () => {
      const [subRes, billsRes, plansRes] = await Promise.all([
        subscriptionsAPI.getSubscription(),
        billsAPI.getBills(),
        subscriptionsAPI.getPlans({ role: 'lab' })
      ]);
      if (subRes.data) setSubscription(subRes.data);
      if (billsRes.data?.bills) setBills(billsRes.data.bills);
      if (plansRes.data?.byRole?.lab?.length) setLabPlans(plansRes.data.byRole.lab);
      else if (plansRes.data?.plans?.length) {
        setLabPlans(plansRes.data.plans.filter((p) => p.role === 'lab'));
      }
      return subRes.data;
    };

    (async () => {
      const maxAttempts = 18;
      const delayMs = 750;
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelled) return;
        try {
          const data = await refreshBundle();
          if (data?.subscription?.stripeSubscriptionId) {
            showSuccess(
              'You’re all set. Your subscription is active — your current plan and trial dates are shown below.'
            );
            return;
          }
        } catch (err) {
          console.error('Post-checkout subscription refresh:', err);
        }
        await new Promise((r) => setTimeout(r, delayMs));
      }
      if (cancelled) return;
      await fetchSubscriptionAndBills();
      showInfo(
        'Payment received. If your plan still looks wrong, wait a moment and refresh the page (Stripe may still be updating your account).'
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [showInfo, showSuccess]);

  useEffect(() => {
    fetchSubscriptionAndBills();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    let cancelled = false;
    setDemographicsStatsLoading(true);
    (async () => {
      try {
        const [statsRes, analyticsRes] = await Promise.all([
          labAPI.getDemographicsStats(),
          labAPI.getAnalytics()
        ]);
        if (!cancelled) {
          setDemographicsStats(statsRes.data);
          setAnalytics(analyticsRes.data);
        }
      } catch (err) {
        console.error('Analytics tab:', err);
        if (!cancelled) {
          setDemographicsStats(null);
          setAnalytics(null);
        }
      } finally {
        if (!cancelled) setDemographicsStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    let cancelled = false;
    setDemoFilteredLoading(true);
    (async () => {
      try {
        const params = {};
        if (demoAnalyticsMonth) params.month = demoAnalyticsMonth;
        if (demoAnalyticsDay) params.day = demoAnalyticsDay;
        if (demoAnalyticsClinicId) params.clinicId = demoAnalyticsClinicId;
        if (demoAnalyticsPanel && demoAnalyticsPanel !== 'all') params.panel = demoAnalyticsPanel;
        const { data } = await labAPI.getDemographicsAnalytics(params);
        if (!cancelled) setDemoFilteredStats(data);
      } catch (err) {
        console.error('Demographics analytics:', err);
        if (!cancelled) setDemoFilteredStats(null);
      } finally {
        if (!cancelled) setDemoFilteredLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, demoAnalyticsMonth, demoAnalyticsDay, demoAnalyticsClinicId, demoAnalyticsPanel]);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await labAPI.getDemographicsPanelOptions();
        if (!cancelled) setDemoPanelOptions(Array.isArray(data?.options) ? data.options : []);
      } catch (err) {
        console.error('Demographics panel options:', err);
        if (!cancelled) setDemoPanelOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const mergedDemoPanelOptions = useMemo(() => {
    const out = [...demoPanelOptions];
    if (demoAnalyticsPanel && demoAnalyticsPanel !== 'all' && !out.some((o) => o === demoAnalyticsPanel)) {
      out.push(demoAnalyticsPanel);
      out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return out;
  }, [demoPanelOptions, demoAnalyticsPanel]);

  useEffect(() => {
    if (user?.labLogoUrl) {
      setLabLogoUrl(user.labLogoUrl);
      localStorage.setItem('labLogoUrl', user.labLogoUrl);
    }
  }, [user?.labLogoUrl]);

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      const loc = formData.pickupRequestId && pickupRequests.find(p => p._id === formData.pickupRequestId)
        ? pickupRequests.find(p => p._id === formData.pickupRequestId).pickupAddress
        : formData.location || {};
      const { data } = await labAPI.createJob({
        pickupRequestId: formData.pickupRequestId,
        title: formData.title || 'Blood Sample Collection',
        description: formData.description || '',
        location: loc,
        scheduledDate: formData.scheduledDate,
        paymentAmount: formData.paymentAmount
      });
      setJobs(prev => [data.job, ...prev]);
      showSuccess('Job created successfully!');
      setFormData({});
      setShowCreateJobModal(false);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to create job');
    }
  };

  const handleAcceptBid = async (jobId, bidId) => {
    try {
      const { data } = await labAPI.acceptBid(jobId, bidId);
      setJobs(prev => prev.map(j => j._id === jobId ? data.job : j));
      showSuccess('Bid accepted! Phlebotomist assigned.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to accept bid');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpdateJobStatus = (jobId, newStatus) => {
    setJobs(prev => prev.map(job => 
      job._id === jobId ? { ...job, status: newStatus } : job
    ));
    showSuccess(`Job status updated to ${newStatus}!`);
  };

  const handleUpdatePickupStatus = async (requestId, payload) => {
    try {
      const { data } = await labAPI.updatePickupStatus(requestId, payload);
      setPickupRequests(prev => prev.map(req => req._id === requestId ? data.request : req));
      showSuccess(data.message || 'Pickup status updated');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update pickup status');
    }
  };

  const handleNotifyClinicDelay = async (requestId, delayNote) => {
    if (!delayNote?.trim()) return;
    try {
      const { data } = await labAPI.notifyClinicDelay(requestId, { delayNote });
      setPickupRequests(prev => prev.map(req => req._id === requestId ? data.request : req));
      showSuccess('Clinic notified of delay');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed');
    }
  };

  const handleAcceptReagentOffer = async (requestId, offerId) => {
    try {
      const { data } = await labAPI.acceptReagentOffer(requestId, offerId);
      setReagentRequests(prev => prev.map(r => r._id === requestId ? data.request : r));
      showSuccess('Reagent offer accepted! Order placed.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to accept offer');
    }
  };

  const handleRejectReagentOffer = (requestId, offerId) => {
    setReagentRequests(prev => prev.map(req => {
      if (req._id === requestId) {
        return {
          ...req,
          offers: req.offers?.map(offer => 
            offer._id === offerId 
              ? { ...offer, status: 'rejected' }
              : offer
          )
        };
      }
      return req;
    }));
    showInfo('Reagent offer rejected.');
  };

  const handlePostReagentRequest = async (e) => {
    e.preventDefault();
    try {
      const { data } = await labAPI.createReagentRequest({
        title: formData.reagentTitle || 'New Reagent Request',
        description: formData.reagentDescription || 'Reagent supply needed',
        reagents: [{ name: formData.reagentName || 'Reagent', quantity: parseInt(formData.reagentQty, 10) || 50, unit: 'bottles', specifications: formData.specs || 'Standard' }],
        requiredBy: formData.requiredBy ? new Date(formData.requiredBy) : undefined,
        budget: parseInt(formData.budget, 10) || 2000
      });
      setReagentRequests(prev => [data.request, ...prev]);
      setFormData({ ...formData, reagentTitle: '', reagentDescription: '', reagentName: '', reagentQty: '', requiredBy: '', budget: '', specs: '' });
      showSuccess('Reagent request posted!');
      setShowReagentModal(false);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to post reagent request');
    }
  };

  const handleViewDetails = (item, type) => {
    setSelectedItem({ item, type });
    setShowDetails(true);
  };

  const handleUploadReport = async (e) => {
    e.preventDefault();
    try {
      const { data } = await labAPI.uploadReport({
        clinic: formData.clinicId,
        patientName: formData.patientName,
        panel: formData.panel,
        jobId: formData.jobId,
        pickupRequestId: formData.pickupRequestId,
        tests: formData.tests ? (typeof formData.tests === 'string' ? JSON.parse(formData.tests) : formData.tests) : []
      });
      setReports(prev => [data.report, ...prev]);
      showSuccess('Report uploaded successfully!');
      setFormData({});
      setShowUploadReportModal(false);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to upload report');
    }
  };

  const filteredLabPickups = useMemo(() => {
    let rows = dashboard.pickupRequests || [];
    if (labPickupStatusFilter) rows = rows.filter((r) => r.status === labPickupStatusFilter);
    return filterBySearch(rows, labPickupSearch, (r) => [
      r.patientName,
      r.status,
      r.urgency,
      r.clinic?.clinicName,
      r.clinic?.name,
      ...(r.tests || []).map((t) => t.testName)
    ]);
  }, [dashboard.pickupRequests, labPickupSearch, labPickupStatusFilter]);

  const filteredLabJobs = useMemo(() => {
    let rows = dashboard.jobs || [];
    if (labJobStatusFilter) rows = rows.filter((j) => j.status === labJobStatusFilter);
    return filterBySearch(rows, labJobSearch, (j) => [
      j.title,
      j.description,
      j.status,
      j.location?.city,
      j.location?.address,
      j.assignedPhlebotomist?.name
    ]);
  }, [dashboard.jobs, labJobSearch, labJobStatusFilter]);

  const filteredLabReagents = useMemo(() => {
    let rows = dashboard.reagentRequests || [];
    if (labReagentStatusFilter) rows = rows.filter((r) => r.status === labReagentStatusFilter);
    return filterBySearch(rows, labReagentSearch, (r) => [
      r.title,
      r.description,
      r.status,
      ...(r.reagents || []).map((x) => x.name)
    ]);
  }, [dashboard.reagentRequests, labReagentSearch, labReagentStatusFilter]);

  const filteredLabReports = useMemo(
    () =>
      filterBySearch(dashboard.reports || [], labReportSearch, (r) => [
        r.reportNumber,
        r.patientName,
        r.panel,
        r.clinic?.clinicName,
        r.clinic?.name,
        ...(r.tests || []).map((t) => t.testName)
      ]),
    [dashboard.reports, labReportSearch]
  );

  const filteredLabBills = useMemo(
    () =>
      filterBySearch(bills || [], labBillsSearch, (b) => [
        b.invoiceNumber,
        b.type,
        b.status,
        b.biller?.name,
        b.biller?.companyName,
        b.amount
      ]),
    [bills, labBillsSearch]
  );

  const filteredLabClinics = useMemo(
    () =>
      filterBySearch(clinics || [], labClinicsSearch, (c) => [
        c.clinicName,
        c.name,
        c.email,
        c.address,
        c.phone,
        c._id
      ]),
    [clinics, labClinicsSearch]
  );

  const pickupStartIndex = (pickupPage - 1) * itemsPerPage;
  const pickupEndIndex = pickupStartIndex + itemsPerPage;
  const paginatedPickups = filteredLabPickups.slice(pickupStartIndex, pickupEndIndex);
  const totalPickupPages = Math.max(1, Math.ceil(filteredLabPickups.length / itemsPerPage));

  const jobsStartIndex = (jobsPage - 1) * itemsPerPage;
  const jobsEndIndex = jobsStartIndex + itemsPerPage;
  const paginatedJobs = filteredLabJobs.slice(jobsStartIndex, jobsEndIndex);
  const totalJobsPages = Math.max(1, Math.ceil(filteredLabJobs.length / itemsPerPage));

  const reagentsStartIndex = (reagentsPage - 1) * itemsPerPage;
  const reagentsEndIndex = reagentsStartIndex + itemsPerPage;
  const paginatedReagents = filteredLabReagents.slice(reagentsStartIndex, reagentsEndIndex);
  const totalReagentsPages = Math.max(1, Math.ceil(filteredLabReagents.length / itemsPerPage));

  const reportsStartIndex = (reportsPage - 1) * itemsPerPage;
  const reportsEndIndex = reportsStartIndex + itemsPerPage;
  const paginatedReports = filteredLabReports.slice(reportsStartIndex, reportsEndIndex);
  const totalReportsPages = Math.max(1, Math.ceil(filteredLabReports.length / itemsPerPage));

  const getPageTitle = () => {
    switch (activeTab) {
      case 'jobs': return 'Jobs';
      case 'pickup-requests': return 'Pickup Requests';
      case 'reagents': return 'Reagent Requests';
      case 'reports': return 'Reports';
      case 'bills': return 'Bills';
      case 'clinics': return 'Clinics';
      case 'employees': return 'Employees';
      case 'analytics': return 'Analytics';
      case 'community': return 'Community & News';
      case 'messaging': return 'Messages';
      case 'settings': return 'Settings';
      case 'service-requests': return 'Clinic requests';
      case 'demographics': return 'Demographics';
      case 'audit-logs': return 'Audit log';
      default: return 'Overview';
    }
  };

  const handleLabLogoChange = async (e) => {
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    try {
      const { data } = await labAPI.uploadLabLogo(fd);
      const url = data.labLogoUrl;
      if (url) {
        setLabLogoUrl(url);
        localStorage.setItem('labLogoUrl', url);
      }
      await refreshUser();
      showSuccess('Logo updated. It will appear on the lab login page.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to upload logo');
    }
    input.value = '';
  };

  return (
    <div>
      {activeTab !== 'overview' && activeTab !== 'demographics' && (
        <h1 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: 'var(--text-primary)', 
          margin: '0 0 8px 0',
          paddingTop: '0'
        }}>
          {getPageTitle()}
        </h1>
      )}
      <LabExtendedFeatures activeTab={activeTab} clinics={clinics} />

      {loading && activeTab === 'overview' && <DashboardSectionLoader />}
      {activeTab === 'overview' && dashboard && !loading && (
        <>
        <div className="quick-actions">
          <Link to="/dashboard?tab=jobs" className="quick-action-btn">
            <span className="quick-action-icon"><WorkIcon sx={{ fontSize: 28 }} /></span>
            <span>Jobs</span>
          </Link>
          <Link to="/dashboard?tab=pickup-requests" className="quick-action-btn">
            <span className="quick-action-icon"><LocalShippingIcon sx={{ fontSize: 28 }} /></span>
            <span>Pickup Requests</span>
          </Link>
          <Link to="/dashboard?tab=reagents" className="quick-action-btn">
            <span className="quick-action-icon"><ScienceIcon sx={{ fontSize: 28 }} /></span>
            <span>Reagent Requests</span>
          </Link>
          <Link to="/dashboard?tab=reports" className="quick-action-btn">
            <span className="quick-action-icon"><AssessmentIcon sx={{ fontSize: 28 }} /></span>
            <span>Reports</span>
          </Link>
          <Link to="/dashboard?tab=clinics" className="quick-action-btn">
            <span className="quick-action-icon"><BusinessCenterIcon sx={{ fontSize: 28 }} /></span>
            <span>Clinics</span>
          </Link>
          <Link to="/dashboard?tab=community" className="quick-action-btn">
            <span className="quick-action-icon"><ForumIcon sx={{ fontSize: 28 }} /></span>
            <span>Community</span>
          </Link>
          <Link to="/dashboard?tab=messaging" className="quick-action-btn">
            <span className="quick-action-icon"><ChatIcon sx={{ fontSize: 28 }} /></span>
            <span>Messages</span>
          </Link>
        </div>
        {subscription?.blocked && (
          <div className="card" style={{ marginBottom: '24px', padding: '16px', background: 'linear-gradient(135deg, rgba(255, 100, 100, 0.2), rgba(255, 100, 100, 0.05))', border: '1px solid rgba(255, 100, 100, 0.4)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#e53935' }}>Trial Expired</h3>
            <p style={{ margin: 0, fontWeight: '600' }}>Your free trial has ended. Upgrade to continue adding clinics, creating jobs, and more.</p>
            {subscription?.trialInvoice && (
              <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem' }}>Invoice: {subscription.trialInvoice.invoiceNumber} - ${subscription.trialInvoice.amount}</p>
            )}
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const trialBillId = subscription?.trialInvoice?._id;
                  if (trialBillId) {
                    startBillCheckout(trialBillId);
                    return;
                  }
                  if (labPlans.length) {
                    openSubscribeModal(labPlans[0]._id);
                    return;
                  }
                  const el = typeof document !== 'undefined' && document.getElementById('lab-subscription-plans');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Pay with card (Stripe)
              </button>
              <Link to="/dashboard?tab=bills" className="btn btn-outline" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                Bills & invoices
              </Link>
            </div>
          </div>
        )}
        {(subscription?.trialActive && !subscription?.blocked && !labSubscriptionMeta.hasStripeSubscription) && (
          <div className="card" style={{ marginBottom: '24px', padding: '16px', background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.15), rgba(181, 255, 51, 0.05))', border: '1px solid rgba(181, 255, 51, 0.3)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: 'var(--logo-lime-green)' }}>
              Free trial
            </h3>
            <p style={{ margin: 0, fontWeight: '600' }}>
              {subscription?.trialDaysRemaining != null && subscription.trialDaysRemaining > 0
                ? `${subscription.trialDaysRemaining} day${subscription.trialDaysRemaining === 1 ? '' : 's'} left in your trial`
                : 'Trial'}
              {subscription?.trialEndsAt
                ? ` · Ends ${formatLocalDate(subscription.trialEndsAt)}`
                : ''}
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Full access during trial. Choose a plan before trial ends.
            </p>
          </div>
        )}
        <div id="lab-subscription-plans" className="card" style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: 'var(--logo-cyan-teal)' }}>Subscription &amp; plans</h3>
          <p style={{ margin: 0, fontWeight: '600' }}>
            Current plan:{' '}
            {labSubscriptionMeta.planDisplayName || subscription?.subscription?.type || 'Free'}
            {subscription?.subscription?.billingCycle ? ` (${subscription.subscription.billingCycle})` : ''}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Clinics: {clinics?.length || 0} · Job posts this month: {jobs?.filter(j => new Date(j.createdAt).getMonth() === new Date().getMonth()).length || 0}</p>
          {labSubscriptionMeta.hasPaidPlanAssigned ? (
            <>
              {labSubscriptionMeta.hasStripeSubscription ? (
                <p style={{ margin: '12px 0 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Stripe payments
                  {subscription?.trialActive && subscription?.trialEndsAt && (
                    <> · Intro period until {formatLocalDate(subscription.trialEndsAt)}</>
                  )}
                  {!subscription?.trialActive && labSubscriptionMeta.endDate && (
                    <> · Renews through {formatLocalDate(labSubscriptionMeta.endDate)}</>
                  )}
                </p>
              ) : (
                <p style={{ margin: '12px 0 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Your plan is active.
                </p>
              )}
              {labPlans.length > 0 ? (
                <div style={{ marginTop: '14px' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ padding: '10px 18px', fontSize: '0.875rem' }}
                    onClick={() => setManageSubscriptionOpen(true)}
                  >
                    Manage subscription
                  </button>
                </div>
              ) : (
                <p style={{ marginTop: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  No plans loaded yet. Ensure the server database is seeded with subscription plans.
                </p>
              )}
            </>
          ) : (
            <>
              <p style={{ margin: '12px 0 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Pick a plan below, then confirm payment schedule and checkout in the next step.
              </p>
              {labPlans.length > 0 ? (
                <LabPlanCardsGrid
                  labPlans={labPlans}
                  labSubscriptionMeta={labSubscriptionMeta}
                  onPlanClick={(planId) => openSubscribeModal(planId)}
                />
              ) : (
                <p style={{ marginTop: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  No plans loaded yet. Ensure the server database is seeded with subscription plans.
                </p>
              )}
            </>
          )}
        </div>
        <Modal
          isOpen={manageSubscriptionOpen}
          onClose={() => setManageSubscriptionOpen(false)}
          title="Change plan"
          wide
        >
          <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            Choose a different tier to start checkout. Your current plan stays active until the new subscription is confirmed.
          </p>
          {labPlans.length > 0 ? (
            <LabPlanCardsGrid
              labPlans={labPlans}
              labSubscriptionMeta={labSubscriptionMeta}
              onPlanClick={(planId) => {
                setManageSubscriptionOpen(false);
                openSubscribeModal(planId);
              }}
              gridStyle={{ marginTop: '8px' }}
            />
          ) : (
            <p style={{ marginTop: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>No plans available.</p>
          )}
        </Modal>
        <div className="grid grid-2">
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Pickup Requests</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
              {dashboard.pickupRequests?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Total requests</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>Active Jobs</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>
              {dashboard.jobs?.filter(j => j.status !== 'completed').length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>In progress</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Reports</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
              {dashboard.reports?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Total reports</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>Reagent Requests</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>
              {dashboard.reagentRequests?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Pending orders</p>
          </div>
        </div>
        </>
      )}

      {activeTab === 'jobs' && (
        <div>
          <Modal isOpen={showCreateJobModal} onClose={() => setShowCreateJobModal(false)} title="Create New Job">
            <form onSubmit={handleCreateJob}>
              <div className="form-group">
                <label className="form-label">From Pickup Request (optional)</label>
                <select className="form-select" value={formData.pickupRequestId || ''} onChange={(e) => {
                  const pr = pickupRequests.find(p => p._id === e.target.value);
                  setFormData({
                    ...formData,
                    pickupRequestId: e.target.value || undefined,
                    title: pr ? `Pickup: ${pr.patientName}` : formData.title,
                    description: pr?.panel || formData.description,
                    scheduledDate: pr?.preferredDate ? new Date(pr.preferredDate).toISOString().slice(0, 16) : formData.scheduledDate,
                    paymentAmount: formData.paymentAmount
                  });
                }}>
                  <option value="">None</option>
                  {pickupRequests.filter(p => p.status === 'confirmed' || p.status === 'pending').map((p) => (
                    <option key={p._id} value={p._id}>{p.patientName} - {p.clinic?.clinicName}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Scheduled Date</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.scheduledDate || ''}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Amount ($)</label>
                <input type="number" className="form-input" value={formData.paymentAmount || ''} onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })} placeholder="Amount for phlebotomist" min={0} step={0.01} />
              </div>
              <div className="form-group">
                <label className="form-label">Request Phlebotomist Directly (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.requestPhlebotomistId || ''}
                  onChange={(e) => setFormData({ ...formData, requestPhlebotomistId: e.target.value })}
                  placeholder="Phlebotomist ID or name to request directly"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Lab can request a specific phlebotomist for this job instead of accepting bids.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Attach media (optional)</label>
                <input type="file" accept="image/*,.pdf" multiple className="form-input" style={{ padding: '8px' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary">Create Job</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowCreateJobModal(false)}>Cancel</button>
              </div>
            </form>
          </Modal>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Jobs</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button type="button" className="btn btn-primary" onClick={() => setShowCreateJobModal(true)}>Create Job</button>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{dashboard.jobs?.length || 0}</span>
              </div>
            </div>
            <TableToolbar
              search={labJobSearch}
              onSearchChange={setLabJobSearch}
              placeholder="Search jobs: title, description, status, location, assignee…"
              resultCount={filteredLabJobs.length}
              totalCount={dashboard.jobs?.length || 0}
            >
              <select
                className="form-select"
                value={labJobStatusFilter}
                onChange={(e) => setLabJobStatusFilter(e.target.value)}
                aria-label="Filter jobs by status"
              >
                <option value="">All statuses</option>
                <option value="open">open</option>
                <option value="bidding">bidding</option>
                <option value="pending_acceptance">pending_acceptance</option>
                <option value="assigned">assigned</option>
                <option value="in-progress">in-progress</option>
                <option value="completed">completed</option>
                <option value="cancelled">cancelled</option>
              </select>
            </TableToolbar>
            <div style={{ display: 'grid', gap: '16px' }}>
              {paginatedJobs.map((job) => (
                <div key={job._id} className="card" style={{ 
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.25rem' }}>{job.title}</h3>
                      <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{job.description}</p>
                    </div>
                    <span className={`badge ${
                      job.status === 'completed' ? 'badge-success' :
                      job.status === 'in-progress' ? 'badge-info' :
                      job.status === 'assigned' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Location</p>
                      <p style={{ margin: 0, fontWeight: '500' }}>{job.location?.address}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment</p>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>${job.paymentAmount}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Assigned To</p>
                      <p style={{ margin: 0, fontWeight: '500' }}>{job.assignedPhlebotomist?.name || 'Unassigned'}</p>
                    </div>
                  </div>
                    {job.bids?.length > 0 && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                      <h4 style={{ color: 'var(--logo-cyan-teal)', marginBottom: '12px', fontSize: '1rem' }}>Bids ({job.bids.length})</h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {job.bids.map((bid) => (
                          <div key={bid._id} style={{ 
                            padding: '12px', 
                            background: 'var(--bg-secondary)', 
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>{bid.phlebotomist?.name}</p>
                              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                ${bid.amount} • {bid.estimatedTime}
                              </p>
                            </div>
                            {bid.status === 'pending' && (
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '6px 16px', fontSize: '0.875rem' }}
                                onClick={() => handleAcceptBid(job._id, bid._id)}
                              >
                                Accept
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                      Message
                    </Link>
                    {job.status === 'completed' && job.paymentStatus !== 'paid' && job.assignedPhlebotomist && (
                      <button
                        className="btn btn-primary"
                        style={{ padding: '6px 16px', fontSize: '0.875rem' }}
                        onClick={async () => {
                          try {
                            await labAPI.markJobPaid(job._id);
                            showSuccess('Phlebotomist marked as paid');
                            fetchData();
                          } catch (err) {
                            showError(err.response?.data?.message || 'Failed to mark paid');
                          }
                        }}
                      >
                        Mark Phlebotomist Paid
                      </button>
                    )}
                    {job.paymentStatus === 'paid' && <span className="badge badge-success">Paid</span>}
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={jobsPage}
              totalPages={totalJobsPages}
              onPageChange={setJobsPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredLabJobs.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'pickup-requests' && (
        <div>
          <Modal
            isOpen={showDetails && selectedItem?.type === 'pickup'}
            onClose={() => { setShowDetails(false); setSelectedItem(null); }}
            title="Pickup request details"
            wide
          >
            {selectedItem?.type === 'pickup' && (
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Patient</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedItem.item.patientName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Clinic</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedItem.item.clinic?.clinicName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Preferred Date</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(selectedItem.item.preferredDate)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Status</p>
                    <span className={`badge ${
                      selectedItem.item.status === 'completed' ? 'badge-success' :
                      selectedItem.item.status === 'in-progress' ? 'badge-info' :
                      selectedItem.item.status === 'assigned' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {selectedItem.item.status}
                    </span>
                  </div>
                </div>
                {selectedItem.item.delayNote && (
                  <div style={{ padding: '12px', background: 'rgba(255,180,0,0.15)', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Lab delay notice</p>
                    <p style={{ margin: 0 }}>{selectedItem.item.delayNote}</p>
                  </div>
                )}
                {selectedItem.item.status !== 'completed' && selectedItem.item.status !== 'declined' && (
                  <div className="dashboard-toolbar-row" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="text" className="form-input" id="lab-pickup-delay-note" placeholder="Notify clinic of test delivery delay..." style={{ flex: 1, minWidth: '200px' }} />
                    <button type="button" className="btn btn-outline" style={{ padding: '6px 16px' }} onClick={() => {
                      const note = document.getElementById('lab-pickup-delay-note')?.value;
                      if (note) handleNotifyClinicDelay(selectedItem.item._id, note);
                    }}>Notify delay</button>
                  </div>
                )}
                {selectedItem.item.tests && selectedItem.item.tests.length > 0 && (
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Tests</p>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {selectedItem.item.tests.map((test, idx) => (
                        <div key={idx} style={{ padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
                          <p style={{ margin: 0, fontWeight: '500' }}>{test.testName}</p>
                          {test.testCode && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              Code: {test.testCode}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Pickup Requests</h2>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.pickupRequests?.length || 0}
              </div>
            </div>
            <TableToolbar
              search={labPickupSearch}
              onSearchChange={setLabPickupSearch}
              placeholder="Search patient, clinic, tests, status…"
              resultCount={filteredLabPickups.length}
              totalCount={dashboard.pickupRequests?.length || 0}
            >
              <select
                className="form-select"
                value={labPickupStatusFilter}
                onChange={(e) => setLabPickupStatusFilter(e.target.value)}
                aria-label="Filter pickups by status"
              >
                <option value="">All statuses</option>
                <option value="pending">pending</option>
                <option value="confirmed">confirmed</option>
                <option value="assigned">assigned</option>
                <option value="in-progress">in-progress</option>
                <option value="completed">completed</option>
                <option value="declined">declined</option>
                <option value="cancelled">cancelled</option>
              </select>
            </TableToolbar>
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Clinic</th>
                    <th>Tests</th>
                    <th>Preferred Date</th>
                    <th>Urgency</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPickups.map((req) => (
                  <tr key={req._id}>
                    <td style={{ fontWeight: '500' }}>{req.patientName}</td>
                    <td>{req.clinic?.clinicName}</td>
                    <td>
                      <span style={{ fontSize: '0.875rem' }}>
                        {req.tests?.map(t => t.testName).join(', ')}
                      </span>
                    </td>
                    <td>{formatLocalDate(req.preferredDate)}</td>
                    <td>
                      <span className={`badge ${
                        req.urgency === 'urgent' ? 'badge-danger' :
                        req.urgency === 'stat' ? 'badge-warning' : 'badge-info'
                      }`}>
                        {req.urgency}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        req.status === 'completed' ? 'badge-success' :
                        req.status === 'declined' ? 'badge-danger' :
                        req.status === 'confirmed' ? 'badge-success' :
                        req.status === 'in-progress' ? 'badge-info' :
                        req.status === 'assigned' ? 'badge-warning' : 'badge-info'
                      }`}>
                        {req.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button className="btn btn-outline" onClick={() => handleViewDetails(req, 'pickup')} style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}>View</button>
                        {req.status === 'pending' && (
                          <>
                            <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.875rem' }} onClick={() => handleUpdatePickupStatus(req._id, { action: 'confirm' })}>Confirm</button>
                            <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.875rem' }} onClick={() => handleUpdatePickupStatus(req._id, { action: 'decline' })}>Decline</button>
                          </>
                        )}
                        {req.status !== 'completed' && req.status !== 'pending' && req.status !== 'declined' && (
                          <select className="form-select" value={req.status} onChange={(e) => handleUpdatePickupStatus(req._id, { status: e.target.value })} style={{ padding: '6px 12px', fontSize: '0.875rem', minHeight: '44px', width: 'auto' }}>
                            <option value="confirmed">Confirmed</option>
                            <option value="assigned">Assigned</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <div className="table-mobile-card">
            {paginatedPickups.map((req) => (
              <div key={req._id} className="table-mobile-card-item">
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Patient</span>
                  <span className="table-mobile-card-value" style={{ fontWeight: '500' }}>{req.patientName}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Clinic</span>
                  <span className="table-mobile-card-value">{req.clinic?.clinicName}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Tests</span>
                  <span className="table-mobile-card-value" style={{ fontSize: '0.875rem' }}>
                    {req.tests?.map(t => t.testName).join(', ')}
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Preferred Date</span>
                  <span className="table-mobile-card-value">{formatLocalDate(req.preferredDate)}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Urgency</span>
                  <span className="table-mobile-card-value">
                    <span className={`badge ${
                      req.urgency === 'urgent' ? 'badge-danger' :
                      req.urgency === 'stat' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {req.urgency}
                    </span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Status</span>
                  <span className="table-mobile-card-value">
                    <span className={`badge ${
req.status === 'completed' ? 'badge-success' :
                        req.status === 'declined' ? 'badge-danger' :
                        req.status === 'confirmed' ? 'badge-success' :
                        req.status === 'in-progress' ? 'badge-info' :
                        req.status === 'assigned' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {req.status}
                    </span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Actions</span>
                  <span className="table-mobile-card-value">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                      <button 
                        className="btn btn-outline" 
                        onClick={() => handleViewDetails(req, 'pickup')}
                        style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                      >
                        View
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleUpdatePickupStatus(req._id, { action: 'confirm' })}>Confirm</button>
                          <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => handleUpdatePickupStatus(req._id, { action: 'decline' })}>Decline</button>
                        </>
                      )}
                      {req.status !== 'completed' && req.status !== 'pending' && req.status !== 'declined' && (
                        <select className="form-select" value={req.status} onChange={(e) => handleUpdatePickupStatus(req._id, { status: e.target.value })} style={{ padding: '8px 12px', width: '100%' }}>
                          <option value="confirmed">Confirmed</option>
                          <option value="assigned">Assigned</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                    </div>
                  </span>
                </div>
              </div>
            ))}
            </div>
            <Pagination
              currentPage={pickupPage}
              totalPages={totalPickupPages}
              onPageChange={setPickupPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredLabPickups.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'reagents' && (
        <div>
          <Modal isOpen={showReagentModal} onClose={() => setShowReagentModal(false)} title="Post New Reagent Request">
            <form onSubmit={handlePostReagentRequest}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input type="text" className="form-input" value={formData.reagentTitle || ''} onChange={(e) => setFormData({ ...formData, reagentTitle: e.target.value })} placeholder="e.g. Monthly supply" />
                </div>
                <div className="form-group">
                  <label className="form-label">Reagent name</label>
                  <input type="text" className="form-input" value={formData.reagentName || ''} onChange={(e) => setFormData({ ...formData, reagentName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input type="number" className="form-input" value={formData.reagentQty || ''} onChange={(e) => setFormData({ ...formData, reagentQty: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Budget ($)</label>
                  <input type="number" className="form-input" value={formData.budget || ''} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Required by</label>
                  <input type="date" className="form-input" value={formData.requiredBy || ''} onChange={(e) => setFormData({ ...formData, requiredBy: e.target.value })} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={formData.reagentDescription || ''} onChange={(e) => setFormData({ ...formData, reagentDescription: e.target.value })} rows={2} />
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Attach picture (optional)</label>
                <input type="file" accept="image/*" className="form-input" style={{ padding: '8px' }} />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Upload a photo of the reagent or sample</p>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary">Post Request</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowReagentModal(false)}>Cancel</button>
              </div>
            </form>
          </Modal>
          <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>Reagent Requests</h2>
            <button type="button" className="btn btn-primary" onClick={() => setShowReagentModal(true)}>Post Reagent Request</button>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.reagentRequests?.length || 0}
              </div>
          </div>
          <TableToolbar
            search={labReagentSearch}
            onSearchChange={setLabReagentSearch}
            placeholder="Search reagent requests…"
            resultCount={filteredLabReagents.length}
            totalCount={dashboard.reagentRequests?.length || 0}
          >
            <select
              className="form-select"
              value={labReagentStatusFilter}
              onChange={(e) => setLabReagentStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="open">open</option>
              <option value="bidding">bidding</option>
              <option value="ordered">ordered</option>
              <option value="delivered">delivered</option>
            </select>
          </TableToolbar>
          <div style={{ display: 'grid', gap: '16px' }}>
            {paginatedReagents.map((req) => (
              <div key={req._id} className="card" style={{ 
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.25rem' }}>{req.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{req.description}</p>
                  </div>
                  <span className={`badge ${
                    req.status === 'delivered' ? 'badge-success' :
                    req.status === 'ordered' ? 'badge-info' : 'badge-warning'
                  }`}>
                    {req.status}
                  </span>
                </div>
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Reagents:</p>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {req.reagents?.map((reagent, idx) => (
                      <div key={idx} style={{ padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                        <p style={{ margin: 0, fontWeight: '500' }}>{reagent.name}</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {reagent.quantity} {reagent.unit} • {reagent.specifications}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                {req.offers?.length > 0 && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <h4 style={{ color: 'var(--logo-cyan-teal)', marginBottom: '12px', fontSize: '1rem' }}>Offers ({req.offers.length})</h4>
                    {req.offers.map((offer) => (
                      <div key={offer._id} style={{ 
                        padding: '12px', 
                        background: 'var(--bg-secondary)', 
                        borderRadius: '8px',
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <div>
                          <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>{offer.supplier?.companyName}</p>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            ${offer.totalPrice} • {offer.message}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem', textDecoration: 'none' }}>Message</Link>
                          {req.status === 'bidding' && offer.status === 'pending' && (
                            <>
                              <button type="button" className="btn btn-secondary" style={{ padding: '6px 16px', fontSize: '0.875rem' }} onClick={() => handleAcceptReagentOffer(req._id, offer._id)}>Accept</button>
                              <button type="button" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem' }} onClick={() => handleRejectReagentOffer(req._id, offer._id)}>Reject</button>
                            </>
                          )}
                        </div>
                        {offer.status === 'accepted' && <span className="badge badge-success">Accepted</span>}
                        {offer.status === 'rejected' && <span className="badge badge-danger">Rejected</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination
            currentPage={reagentsPage}
            totalPages={totalReagentsPages}
            onPageChange={setReagentsPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredLabReagents.length}
          />
        </div>
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Bills (Invoices to Pay)</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Bills from Phlebotomists, Suppliers, and Admin. Pay via link, cash, or check.</p>
          <TableToolbar
            search={labBillsSearch}
            onSearchChange={setLabBillsSearch}
            placeholder="Search invoice #, biller, type, status…"
            resultCount={filteredLabBills.length}
            totalCount={bills?.length || 0}
          />
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr><th>Invoice</th><th>From</th><th>Amount</th><th>Type</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredLabBills.map((b) => (
                  <tr key={b._id}>
                    <td style={{ fontWeight: '500' }}>{b.invoiceNumber}</td>
                    <td>{b.biller?.name || b.biller?.companyName}</td>
                    <td style={{ fontWeight: '600' }}>${b.amount?.toLocaleString()}</td>
                    <td><span className="badge badge-info">{b.type?.replace('_', ' ') || 'custom'}</span></td>
                    <td><span className={`badge ${b.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span></td>
                    <td>
                      {b.status === 'pending' && (
                        <>
                          {b.paymentLink ? (
                            <a href={b.paymentLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.8125rem', marginRight: '8px' }}>Pay Link</a>
                          ) : (
                            <button type="button" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.8125rem', marginRight: '8px' }} onClick={() => startBillCheckout(b._id)}>
                              Pay online (Stripe)
                            </button>
                          )}
                          <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.8125rem' }} onClick={async () => {
                            try {
                              await billsAPI.markBillPaid(b._id, { paymentMethod: 'cash' });
                              showSuccess('Bill marked as paid');
                              fetchSubscriptionAndBills();
                            } catch (err) { showError(err.response?.data?.message || 'Failed'); }
                          }}>Mark Paid (Cash/Check)</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-mobile-card">
            {filteredLabBills.map((b) => (
              <div key={b._id} className="table-mobile-card-item">
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Invoice</span>
                  <span className="table-mobile-card-value" style={{ fontWeight: '500' }}>{b.invoiceNumber}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">From</span>
                  <span className="table-mobile-card-value">{b.biller?.name || b.biller?.companyName}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Amount</span>
                  <span className="table-mobile-card-value" style={{ fontWeight: '600' }}>${b.amount?.toLocaleString()}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Type</span>
                  <span className="table-mobile-card-value">
                    <span className="badge badge-info">{b.type?.replace('_', ' ') || 'custom'}</span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Status</span>
                  <span className="table-mobile-card-value">
                    <span className={`badge ${b.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Actions</span>
                  <span className="table-mobile-card-value">
                    {b.status === 'pending' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                        {b.paymentLink ? (
                          <a
                            href={b.paymentLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline"
                            style={{ padding: '8px 12px', fontSize: '0.8125rem', minHeight: '40px' }}
                          >
                            Pay Link
                          </a>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline"
                            style={{ padding: '8px 12px', fontSize: '0.8125rem', minHeight: '40px' }}
                            onClick={() => startBillCheckout(b._id)}
                          >
                            Pay online (Stripe)
                          </button>
                        )}
                        <button
                          className="btn btn-primary"
                          style={{ padding: '8px 12px', fontSize: '0.8125rem', minHeight: '40px' }}
                          onClick={async () => {
                            try {
                              await billsAPI.markBillPaid(b._id, { paymentMethod: 'cash' });
                              showSuccess('Bill marked as paid');
                              fetchSubscriptionAndBills();
                            } catch (err) {
                              showError(err.response?.data?.message || 'Failed');
                            }
                          }}
                        >
                          Mark Paid (Cash/Check)
                        </button>
                      </div>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {(!filteredLabBills || filteredLabBills.length === 0) && <p style={{ color: 'var(--text-secondary)', padding: '24px', textAlign: 'center' }}>No bills match</p>}
        </div>
      )}

      {activeTab === 'clinics' && (
        <div>
          <Modal
            isOpen={!!editClinicDraft}
            onClose={() => !editClinicSaving && setEditClinicDraft(null)}
            title="Edit clinic"
          >
            {editClinicDraft && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const id = editClinicDraft._id;
                  setEditClinicSaving(true);
                  try {
                    await labAPI.updateClinic(id, {
                      clinicName: editClinicDraft.clinicName,
                      name: editClinicDraft.name,
                      email: editClinicDraft.email,
                      address: editClinicDraft.address,
                      phone: editClinicDraft.phone
                    });
                    showSuccess('Clinic updated');
                    setEditClinicDraft(null);
                    fetchData();
                  } catch (err) {
                    showError(err.response?.data?.message || 'Could not update clinic');
                  } finally {
                    setEditClinicSaving(false);
                  }
                }}
              >
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-clinic-name">Clinic name</label>
                  <input
                    id="edit-clinic-name"
                    type="text"
                    className="form-input"
                    value={editClinicDraft.clinicName}
                    onChange={(e) => setEditClinicDraft({ ...editClinicDraft, clinicName: e.target.value })}
                    autoComplete="organization"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-clinic-display-name">Contact / display name</label>
                  <input
                    id="edit-clinic-display-name"
                    type="text"
                    className="form-input"
                    value={editClinicDraft.name}
                    onChange={(e) => setEditClinicDraft({ ...editClinicDraft, name: e.target.value })}
                    required
                    maxLength={120}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-clinic-email">Email</label>
                  <input
                    id="edit-clinic-email"
                    type="email"
                    className="form-input"
                    value={editClinicDraft.email}
                    onChange={(e) => setEditClinicDraft({ ...editClinicDraft, email: e.target.value })}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-clinic-phone">Phone</label>
                  <input
                    id="edit-clinic-phone"
                    type="tel"
                    className="form-input"
                    value={editClinicDraft.phone || ''}
                    onChange={(e) => setEditClinicDraft({ ...editClinicDraft, phone: e.target.value })}
                    autoComplete="tel"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-clinic-address">Address</label>
                  <input
                    id="edit-clinic-address"
                    type="text"
                    className="form-input"
                    value={editClinicDraft.address || ''}
                    onChange={(e) => setEditClinicDraft({ ...editClinicDraft, address: e.target.value })}
                    autoComplete="street-address"
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button type="submit" className="btn btn-primary" disabled={editClinicSaving}>
                    {editClinicSaving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={editClinicSaving}
                    onClick={() => setEditClinicDraft(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </Modal>
          <Modal
            isOpen={!!disconnectClinicTarget}
            onClose={() => !disconnectClinicBusy && setDisconnectClinicTarget(null)}
            title="Disconnect clinic?"
          >
            {disconnectClinicTarget && (
              <div style={{ display: 'grid', gap: '16px' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Remove <strong style={{ color: 'var(--text-primary)' }}>{disconnectClinicTarget.label}</strong> from your lab.
                  The clinic account stays active; only your association ends. Demographics rows your lab had linked to this clinic will be unlinked.
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(239, 68, 68, 0.85)',
                      color: '#f87171'
                    }}
                    disabled={disconnectClinicBusy}
                    onClick={async () => {
                      setDisconnectClinicBusy(true);
                      try {
                        await labAPI.disconnectClinic(disconnectClinicTarget._id);
                        showSuccess('Clinic disconnected');
                        setDisconnectClinicTarget(null);
                        fetchData();
                      } catch (err) {
                        showError(err.response?.data?.message || 'Could not disconnect');
                      } finally {
                        setDisconnectClinicBusy(false);
                      }
                    }}
                  >
                    {disconnectClinicBusy ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={disconnectClinicBusy}
                    onClick={() => setDisconnectClinicTarget(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </Modal>
          <Modal isOpen={showAddClinicModal} onClose={() => setShowAddClinicModal(false)} title="Add Clinic">
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Associate a clinic: invite by email (new clinic gets sign-up link) or add existing clinic by ID.</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const clinicId = formData.addClinicId?.trim();
              if (!clinicId) { showInfo('Enter clinic ID to add existing clinic'); return; }
              try {
                await labAPI.addClinic({ clinicId });
                setClinics(prev => [...prev, { _id: clinicId }]);
                showSuccess('Clinic added successfully!');
                setShowAddClinicModal(false);
                fetchData();
              } catch (err) {
                showError(err.response?.data?.message || 'Failed to add clinic');
              }
            }}>
              <div className="form-group">
                <label className="form-label">Invite by Email (for new clinics)</label>
                <input type="email" className="form-input" placeholder="clinic@example.com" value={formData.inviteEmail || ''} onChange={(e) => setFormData({ ...formData, inviteEmail: e.target.value })} />
              </div>
              {formData.inviteEmail && (
                <button type="button" className="btn btn-secondary" style={{ marginBottom: '12px' }} onClick={async () => {
                  try {
                    const { data } = await labAPI.inviteClinic({ email: formData.inviteEmail });
                    showSuccess(data.inviteLink ? `Invite sent! Share: ${data.inviteLink}` : (data.message || 'Invite sent.'));
                    setFormData({ ...formData, inviteEmail: '' });
                    if (data.clinic) setClinics(prev => [...prev, data.clinic]);
                    setShowAddClinicModal(false);
                    fetchData();
                  } catch (err) {
                    showError(err.response?.data?.message || 'Failed');
                  }
                }}>Send Invite</button>
              )}
              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '16px 0' }} />
              <div className="form-group">
                <label className="form-label">Or: Add existing clinic by ID</label>
                <input type="text" className="form-input" placeholder="MongoDB ObjectId of clinic user" value={formData.addClinicId || ''} onChange={(e) => setFormData({ ...formData, addClinicId: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" disabled={!formData.addClinicId}>Add Clinic</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowAddClinicModal(false)}>Cancel</button>
              </div>
            </form>
          </Modal>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Your Clinics</h2>
              <button type="button" className="btn btn-primary" onClick={() => setShowAddClinicModal(true)}>Add Clinic</button>
            </div>
            <TableToolbar
              search={labClinicsSearch}
              onSearchChange={setLabClinicsSearch}
              placeholder="Search clinics by name, email, address…"
              resultCount={filteredLabClinics.length}
              totalCount={clinics?.length || 0}
            />
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr><th>Clinic Name</th><th>Contact</th><th>Address</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredLabClinics.map((c) => (
                    <tr key={c._id}>
                      <td style={{ fontWeight: '500' }}>{c.clinicName || c.name || c._id}</td>
                      <td>{c.email}</td>
                      <td>{c.address || '—'}</td>
                      <td><span className="badge badge-success">active</span></td>
                      <td>
                        {user?.role === 'lab' ? (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-outline"
                              style={{ padding: '6px 16px', fontSize: '0.875rem' }}
                              onClick={() =>
                                setEditClinicDraft({
                                  _id: c._id,
                                  clinicName: c.clinicName || '',
                                  name: c.name || '',
                                  email: c.email || '',
                                  address: c.address || '',
                                  phone: c.phone || ''
                                })
                              }
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline"
                              style={{
                                padding: '6px 16px',
                                fontSize: '0.875rem',
                                borderColor: 'rgba(239, 68, 68, 0.65)',
                                color: '#f87171'
                              }}
                              title="Remove association with this clinic"
                              onClick={() =>
                                setDisconnectClinicTarget({
                                  _id: c._id,
                                  label: c.clinicName || c.name || c.email || String(c._id)
                                })
                              }
                            >
                              Disconnect
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'employees' && (
        <div>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title" style={{ marginBottom: '8px' }}>Employees &amp; tasks</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
              Assign internal work to lab staff or field phlebotomists. Reorder tasks for your team&apos;s workflow; column visibility is saved per browser.
            </p>
          </div>
          <LabTaskTracker />
        </div>
      )}

      {activeTab === 'analytics' && (
        <>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title" style={{ marginBottom: '8px' }}>Demographics — filtered test totals</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px', maxWidth: '920px', lineHeight: 1.45 }}>
              Choose the specimen calendar month (defaults to this month) and optional single day (draw → received → printed, same logic as the demographics sheet), then a provider clinic.
              The headline total and panel cards count only rows whose specimen date falls in that month (e.g. January 2026 when you pick that month). Clear the month field for all-time totals (server-capped). Panel category lists distinct Panel values from your demographics data.
            </p>
            <div className="lab-demo-filters__grid lab-demo-filters__grid--primary" style={{ maxWidth: '960px' }}>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="lab-demo-analytics-month">Month (specimen date)</label>
                <input
                  id="lab-demo-analytics-month"
                  type="month"
                  className="form-input"
                  value={demoAnalyticsMonth}
                  onChange={(e) => setDemoAnalyticsMonth(e.target.value)}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '6px 0 0', lineHeight: 1.35 }}>
                  {demoAnalyticsMonth
                    ? <>Showing {formatAnalyticsMonth(demoAnalyticsMonth)} only. Clear for all dates.</>
                    : <>No month filter — all specimen dates (capped).</>}
                </p>
              </div>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="lab-demo-analytics-day">Single day (optional)</label>
                <input
                  id="lab-demo-analytics-day"
                  type="date"
                  className="form-input"
                  value={demoAnalyticsDay}
                  onChange={(e) => setDemoAnalyticsDay(e.target.value)}
                />
              </div>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="lab-demo-analytics-clinic">Provider (clinic)</label>
                <select
                  id="lab-demo-analytics-clinic"
                  className="form-input"
                  value={demoAnalyticsClinicId}
                  onChange={(e) => setDemoAnalyticsClinicId(e.target.value)}
                >
                  <option value="">All clinics</option>
                  {(clinics || []).map((c) => (
                    <option key={c._id} value={c._id}>{c.clinicName || c.name || c.email || c._id}</option>
                  ))}
                </select>
              </div>
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="lab-demo-analytics-panel">Panel category</label>
                <select
                  id="lab-demo-analytics-panel"
                  className="form-input"
                  value={demoAnalyticsPanel}
                  onChange={(e) => setDemoAnalyticsPanel(e.target.value)}
                  title="Options come from Panel values in your demographics imports"
                >
                  <option value="all">All panels</option>
                  {mergedDemoPanelOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
              {demoFilteredLoading ? (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Calculating…</p>
              ) : (
                <>
                  <p style={{ fontSize: '2rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--logo-lime-green)' }}>
                    {(demoFilteredStats?.testsCounted ?? 0).toLocaleString()}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.45 }}>
                    Individual tests (from ordered tests) · {(demoFilteredStats?.specimenRows ?? 0).toLocaleString()} specimen rows match filters
                    {demoAnalyticsPanel !== 'all' ? ' and selected panel' : ''}
                  </p>
                  {demoFilteredStats?.panelBreakdown?.panels?.length > 0 && (
                    <div style={{ marginTop: '18px' }}>
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.45 }}>
                        Tests by panel for
                        {demoAnalyticsMonth ? ` ${formatAnalyticsMonth(demoAnalyticsMonth)}` : ' the selected filters'}
                        {demoAnalyticsDay ? ` · day ${demoAnalyticsDay}` : ''}
                        {demoAnalyticsClinicId
                          ? ` · ${(clinics || []).find((c) => c._id === demoAnalyticsClinicId)?.clinicName || 'one clinic'}`
                          : ''}
                        {' '}
                        (not affected by the panel dropdown above).
                      </p>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
                          gap: '10px'
                        }}
                      >
                        {demoFilteredStats.panelBreakdown.panels.map((block) => (
                          <div
                            key={block.key}
                            style={{
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '10px 12px'
                            }}
                          >
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={block.label}
                            >
                              {block.label}
                            </div>
                            <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>
                              {(block.testsCounted ?? 0).toLocaleString()} tests
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                              {(block.specimenRows ?? 0).toLocaleString()} specimens
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="grid grid-3" style={{ gap: '24px' }}>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=demographics')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=demographics');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Tests (demographics)</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-cyan-teal)' }}>
              {demographicsStatsLoading ? '…' : (demographicsStats?.testsCounted ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.45 }}>
              {(demographicsStats?.specimenRows ?? 0).toLocaleString()} specimen rows · Click to open the demographics sheet
            </p>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=jobs')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=jobs');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Jobs by month</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-cyan-teal)' }}>
              {demographicsStatsLoading ? '…' : (analytics?.jobTotal ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              All jobs for this lab · Counts bucketed by scheduled month · Click to open Jobs
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.jobsByMonth || []).map((d, i) => (
                <div key={`${d.month}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{formatAnalyticsMonth(d.month)}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
              {!demographicsStatsLoading && !(analytics?.jobsByMonth || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No jobs yet.</p>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-lime-green)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-lime-green) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=pickup-requests')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=pickup-requests');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Pickup Requests</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-lime-green)' }}>
              {demographicsStatsLoading ? '…' : (analytics?.pickupTotal ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Total requests · By created month · Click to open pickup requests
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.pickupsByMonth || []).map((d, i) => (
                <div key={`${d.month}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{formatAnalyticsMonth(d.month)}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-lime-green)' }}>{d.count}</span>
                </div>
              ))}
              {!demographicsStatsLoading && !(analytics?.pickupsByMonth || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No pickup requests yet.</p>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=reports')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=reports');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Reports Generated</h3>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, margin: '8px 0', color: 'var(--logo-cyan-teal)' }}>
              {demographicsStatsLoading ? '…' : (analytics?.reportTotal ?? 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Total reports · By report date · Click to open reports
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.reportsByMonth || []).map((d, i) => (
                <div key={`${d.month}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{formatAnalyticsMonth(d.month)}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
              {!demographicsStatsLoading && !(analytics?.reportsByMonth || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No reports yet.</p>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="card"
            style={{
              gridColumn: '1 / -1',
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--logo-cyan-teal)';
              e.currentTarget.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--logo-cyan-teal) 35%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => navigate('/dashboard?tab=jobs')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/dashboard?tab=jobs');
              }
            }}
          >
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Jobs by Status (This Lab)</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
              {demographicsStatsLoading ? (
                <span>Loading…</span>
              ) : (
                <>
                  <strong>{(analytics?.jobTotal ?? 0).toLocaleString()}</strong> jobs total · Click to manage jobs
                </>
              )}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.jobsByStatus || []).map((d, i) => (
                <div key={`${d.status}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
                  <span>{labJobStatusLabel(d.status)}</span>
                  <span className="badge badge-info">{d.count}</span>
                </div>
              ))}
              {!demographicsStatsLoading && !(analytics?.jobsByStatus || []).length && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No jobs yet.</p>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      {activeTab === 'settings' && (
        <AccountSettingsPanel
          user={user}
          refreshUser={refreshUser}
          onMessage={(m) => {
            if (!m) return;
            const s = String(m).trim();
            if (/^error\s*:/i.test(s)) showError(s.replace(/^error\s*:\s*/i, ''));
            else showSuccess(s);
          }}
          before={(
            <div className="card account-settings-card">
              <h2 className="card-title" style={{ marginBottom: '8px' }}>Lab logo</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Upload your lab logo. It will be shown on the lab login page.</p>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed var(--border-color)', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {labLogoUrl ? (
                    <img src={labLogoUrl} alt="Lab logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>No logo</span>
                  )}
                </div>
                <div>
                  {user?.role === 'lab' ? (
                    <>
                      <label className="form-label">Choose image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLabLogoChange}
                        className="form-input"
                        style={{ padding: '8px', marginTop: '4px' }}
                      />
                    </>
                  ) : (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                      Only the lab owner account can change this logo.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        />
      )}

      {activeTab === 'community' && (
        <div style={{ display: 'grid', gap: '24px' }}>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '12px' }}>Announcements & Industry Updates</h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              {newsAnnouncements.map((n) => (
                <div key={n._id} style={{ padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)' }}>
                  <span className="badge badge-info" style={{ marginRight: '8px' }}>{n.type}</span>
                  <strong>{n.title}</strong>
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{n.summary}</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{formatLocalDate(n.date)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '12px' }}>Discussion Threads</h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              {communityThreads.map((t) => (
                <div key={t._id} style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    {t.pinned && <span className="badge badge-warning" style={{ marginRight: '8px' }}>Pinned</span>}
                    <strong style={{ color: 'var(--logo-cyan-teal)' }}>{t.title}</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>by {t.author?.labName || t.author?.name} · {t.replies} replies</p>
                  </div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{formatLocalDate(t.lastActivity)}</span>
                  <button className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem' }}>View</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'messaging' && (
        <div className="card messaging-card">
          <h2 className="card-title">Messages</h2>
          <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="form-label" style={{ margin: 0 }}>New message</label>
            <select
              className="form-input"
              style={{ maxWidth: '280px' }}
              defaultValue=""
              aria-label="Start conversation with a contact"
              onChange={(e) => {
                const v = e.target.value;
                if (v) startConversation(v);
                e.target.value = '';
              }}
            >
              <option value="">Select clinic or partner…</option>
              {(clinics || []).map((clinic) => (
                <option key={clinic._id} value={clinic._id}>{clinic.clinicName || clinic.name || clinic.email || clinic._id}</option>
              ))}
            </select>
            {messagingLoading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <GradientSpinner size={22} />
                Loading…
              </span>
            )}
          </div>
          <div className="messaging-layout">
            <div className="messaging-conversation-list">
              {conversations.map((conv) => (
                <MessagingConversationListItem
                  key={conv._id}
                  conv={conv}
                  selectedConvId={selectedConv}
                  onSelect={setSelectedConv}
                  title={userDisplayLabel(conv.otherParty)}
                />
              ))}
            </div>
            <div className="messaging-thread">
              {selectedConv ? (
                <>
                  <div className="messaging-messages">
                    {chatMessages.map((msg) => (
                      <MessagingMessageRow key={msg._id} msg={msg} meUser={user} formatLocalDateTime={formatLocalDateTime} />
                    ))}
                  </div>
                  <form onSubmit={sendMessage} className="messaging-composer">
                    <input type="file" accept="image/*,.pdf" multiple style={{ maxWidth: '120px', padding: '4px', fontSize: '0.75rem' }} title="Attach" disabled />
                    <input type="text" className="form-input" placeholder="Type a message..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                    <button type="submit" className="btn btn-primary" disabled={!messageText.trim()}>Send</button>
                  </form>
                </>
              ) : (
                <div className="messaging-empty">Select a conversation</div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div>
          <Modal
            isOpen={showDetails && selectedItem?.type === 'report'}
            onClose={() => { setShowDetails(false); setSelectedItem(null); }}
            title="Report details"
            wide
          >
            {selectedItem?.type === 'report' && (
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Report #</p>
                    <p style={{ margin: 0, fontWeight: '500', fontFamily: 'monospace' }}>{selectedItem.item.reportNumber}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Patient</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedItem.item.patientName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Clinic</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedItem.item.clinic?.clinicName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Date</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(selectedItem.item.reportDate)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Panel</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedItem.item.panel || '—'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment</p>
                    <span className={`badge ${
                      selectedItem.item.billingStatus === 'paid' ? 'badge-success' :
                      selectedItem.item.billingStatus === 'billed' ? 'badge-info' : 'badge-warning'
                    }`}>
                      {selectedItem.item.billingStatus}
                    </span>
                  </div>
                </div>
                {selectedItem.item.tests && selectedItem.item.tests.length > 0 && (
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Tests</p>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {selectedItem.item.tests.map((t, idx) => (
                        <div key={idx} style={{ padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
                          <p style={{ margin: 0, fontWeight: '500' }}>{t.testName}{t.result ? ` — ${t.result}` : ''}</p>
                          {t.testCode && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{t.testCode}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal>
          <Modal isOpen={showUploadReportModal} onClose={() => setShowUploadReportModal(false)} title="Upload Report">
            <form onSubmit={handleUploadReport}>
              <div className="form-group">
                <label className="form-label">Patient Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.patientName || ''}
                  onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Clinic ID</label>
                <input type="text" className="form-input" value={formData.clinicId || ''} onChange={(e) => setFormData({ ...formData, clinicId: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Job ID (optional - links report to job, marks job complete)</label>
                <input type="text" className="form-input" value={formData.jobId || ''} onChange={(e) => setFormData({ ...formData, jobId: e.target.value })} placeholder="MongoDB ObjectId of job" />
              </div>
              <div className="form-group">
                <label className="form-label">Pickup Request ID (optional)</label>
                <input type="text" className="form-input" value={formData.pickupRequestId || ''} onChange={(e) => setFormData({ ...formData, pickupRequestId: e.target.value })} placeholder="Links report to pickup" />
              </div>
              <div className="form-group">
                <label className="form-label">Tests (JSON format)</label>
                <textarea
                  className="form-textarea"
                  value={formData.tests || ''}
                  onChange={(e) => setFormData({ ...formData, tests: e.target.value })}
                  placeholder='[{"testName": "CBC", "testCode": "CBC001", "result": "Normal"}]'
                />
              </div>
              <div className="form-group">
                <label className="form-label">Attach media (optional)</label>
                <input type="file" accept="image/*,.pdf" multiple className="form-input" style={{ padding: '8px' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary">Upload Report</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowUploadReportModal(false)}>Cancel</button>
              </div>
            </form>
          </Modal>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Reports</h2>
              <button type="button" className="btn btn-primary" onClick={() => setShowUploadReportModal(true)}>Upload Report</button>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.reports?.length || 0}
              </div>
            </div>
            <TableToolbar
              search={labReportSearch}
              onSearchChange={setLabReportSearch}
              placeholder="Search reports #, patient, clinic, tests…"
              resultCount={filteredLabReports.length}
              totalCount={dashboard.reports?.length || 0}
            />
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Report Number</th>
                    <th>Patient</th>
                    <th>Clinic</th>
                    <th>Date</th>
                    <th>Tests</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReports.map((report) => (
                    <tr key={report._id}>
                      <td style={{ fontWeight: '500', fontFamily: 'monospace' }}>{report.reportNumber}</td>
                      <td>{report.patientName}</td>
                      <td>{report.clinic?.clinicName}</td>
                      <td>{formatLocalDate(report.reportDate)}</td>
                      <td>
                        <span style={{ fontSize: '0.875rem' }}>
                          {report.tests?.map(t => t.testName).join(', ')}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="btn btn-outline" 
                          onClick={() => handleViewDetails(report, 'report')}
                          style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-mobile-card">
              {paginatedReports.map((report) => (
                <div key={report._id} className="table-mobile-card-item">
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Report #</span>
                    <span className="table-mobile-card-value" style={{ fontWeight: '500', fontFamily: 'monospace' }}>{report.reportNumber}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Patient</span>
                    <span className="table-mobile-card-value">{report.patientName}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Clinic</span>
                    <span className="table-mobile-card-value">{report.clinic?.clinicName}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Date</span>
                    <span className="table-mobile-card-value">{formatLocalDate(report.reportDate)}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Tests</span>
                    <span className="table-mobile-card-value" style={{ fontSize: '0.875rem' }}>
                      {report.tests?.map(t => t.testName).join(', ')}
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Actions</span>
                    <span className="table-mobile-card-value">
                      <button 
                        className="btn btn-outline" 
                        onClick={() => handleViewDetails(report, 'report')}
                        style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                      >
                        View
                      </button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={reportsPage}
              totalPages={totalReportsPages}
              onPageChange={setReportsPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredLabReports.length}
            />
          </div>
        </div>
      )}
      <SubscribeCheckoutModal
        isOpen={subscribeModal.open}
        onClose={() => setSubscribeModal({ open: false, planId: null })}
        plans={labPlans}
        initialPlanId={subscribeModal.planId}
        subscriptionInfo={subscription}
        returnTab={activeTab}
      />
    </div>
  );
};

export default LabDashboard;
